import { DOCUMENT } from '@angular/common';
import {
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import {
  NavigationEnd,
  NavigationError,
  NavigationErrorHandlerFeature,
  Router,
  withNavigationErrorHandler,
} from '@angular/router';
import { filter } from 'rxjs';

/**
 * What the browsers say when a lazy route's chunk is no longer on the server:
 * Chrome, Firefox and Safari each in their own words.
 */
const STALE_CHUNK_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'ChunkLoadError',
];

/** Remembers the one reload already spent on a URL, so a real outage does not loop. */
const RELOADED_FOR_KEY = 'pg-stale-chunk-reload';

/**
 * A navigation failed because the chunk it needed cannot be fetched — the
 * signature of a tab opened before a deploy: every deploy renames the lazy
 * chunks and removes the old files from the stand.
 */
export function isStaleChunkError(error: unknown): boolean {
  if (!error) return false;
  const text = String(
    (error as { message?: unknown }).message ?? error
  ).toLowerCase();
  const name = String((error as { name?: unknown }).name ?? '').toLowerCase();
  return STALE_CHUNK_MESSAGES.some(
    (m) => text.includes(m.toLowerCase()) || name === m.toLowerCase()
  );
}

/** The part of the window the handler touches, so it can be tested without one. */
export interface StaleChunkWindow {
  location: { assign(url: string): void };
  sessionStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
}

/**
 * POC-9609. QA pressed "Merchant history" on a transaction and nothing
 * happened: the tab had been opened before that afternoon's deploy, the
 * merchants chunk had not been visited in it yet, and its pre-deploy name
 * answered 404. The router reported a NavigationError nobody listened to, so
 * the menu closed and the URL stayed. Every deploy does this to every open
 * tab on the first lazy screen it has not loaded yet.
 *
 * The remedy is the usual one: when a navigation fails on a chunk that is no
 * longer there, load the target URL from the server — the fresh index names
 * the fresh chunks. Once per URL: a second failure on the same URL means the
 * server really has no chunk, and reloading again would loop.
 *
 * Returns true when a reload was issued, so the caller (and the spec) can tell.
 */
export function reloadOnStaleChunk(
  error: NavigationError,
  win: StaleChunkWindow
): boolean {
  if (!isStaleChunkError(error.error)) return false;

  const target = error.url || '/';
  let spent: string | null = null;
  try {
    spent = win.sessionStorage.getItem(RELOADED_FOR_KEY);
  } catch {
    // Storage can be blocked (private mode, cleared site data); reload once anyway.
  }
  if (spent === target) {
    console.error(
      `The chunk for ${target} is missing after a reload as well — the stand may be mid-deploy; try again in a minute`
    );
    return false;
  }

  try {
    win.sessionStorage.setItem(RELOADED_FOR_KEY, target);
  } catch {
    // Without storage the loop guard is lost; a reload is still the right call.
  }
  console.warn(
    `The chunk for ${target} is gone from the server (a deploy renamed it) — reloading the page to pick up the new bundle`
  );
  win.location.assign(target);
  return true;
}

/** Forgets the spent reload once a navigation lands, so the next deploy gets its own. */
export function clearStaleChunkReload(win: StaleChunkWindow): void {
  try {
    win.sessionStorage.removeItem(RELOADED_FOR_KEY);
  } catch {
    // Nothing to forget without storage.
  }
}

/** `provideRouter` feature: a stale-chunk navigation error reloads the page onto its target. */
export function withStaleChunkReload(): NavigationErrorHandlerFeature {
  return withNavigationErrorHandler((error) => {
    const win = inject(DOCUMENT)
      .defaultView as unknown as StaleChunkWindow | null;
    if (!win) return;
    reloadOnStaleChunk(error, win);
  });
}

/**
 * The other half: once any navigation lands, the spent reload is forgotten, so
 * the same URL may reload again after the next deploy. Without this the guard
 * key would outlive its deploy and the second stale tab on that URL would be
 * back to a silent no-op.
 */
export function provideStaleChunkReloadReset(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      const win = inject(DOCUMENT)
        .defaultView as unknown as StaleChunkWindow | null;
      if (!win) return;
      inject(Router)
        .events.pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => clearStaleChunkReload(win));
    }),
  ]);
}
